import React from 'react';
import CloseOutlined from '@ant-design/icons/CloseOutlined';
import { mergeProps, pickAttrs } from '@rc-component/util';
import { useLocale } from '../../locale';
import defaultLocale from '../../locale/en_US';
import { isNonNullable, isPlainObject } from '../is';
export const pickClosable = (context) => {
    if (!context) {
        return undefined;
    }
    const { closable, closeIcon } = context;
    return { closable, closeIcon };
};
const EmptyFallbackCloseCollection = {};
const computeClosableConfig = (closable, closeIcon) => {
    if (!closable && (closable === false || closeIcon === false || closeIcon === null)) {
        return false;
    }
    if (!isNonNullable(closable) && !isNonNullable(closeIcon)) {
        return null;
    }
    let closableConfig = {
        closeIcon: typeof closeIcon !== 'boolean' && isNonNullable(closeIcon) ? closeIcon : undefined,
    };
    if (isPlainObject(closable)) {
        closableConfig = { ...closableConfig, ...closable };
    }
    return closableConfig;
};
const mergeClosableConfigs = (propConfig, contextConfig, fallbackConfig) => {
    if (propConfig === false) {
        return false;
    }
    if (propConfig) {
        return mergeProps(fallbackConfig, contextConfig, propConfig);
    }
    if (contextConfig === false) {
        return false;
    }
    if (contextConfig) {
        return mergeProps(fallbackConfig, contextConfig);
    }
    return fallbackConfig.closable ? fallbackConfig : false;
};
const computeCloseIcon = (mergedConfig, fallbackCloseCollection, closeLabel) => {
    const { closeIconRender } = fallbackCloseCollection;
    const { closeIcon, ...restConfig } = mergedConfig;
    let finalCloseIcon = closeIcon;
    const ariaOrDataProps = pickAttrs(restConfig, true);
    if (isNonNullable(finalCloseIcon)) {
        if (closeIconRender) {
            finalCloseIcon = closeIconRender(finalCloseIcon);
        }
        finalCloseIcon = React.isValidElement(finalCloseIcon) ? (React.cloneElement(finalCloseIcon, {
            'aria-label': closeLabel,
            ...finalCloseIcon.props,
            ...ariaOrDataProps,
        })) : (React.createElement("span", { "aria-label": closeLabel, ...ariaOrDataProps }, finalCloseIcon));
    }
    return [finalCloseIcon, ariaOrDataProps];
};
export const computeClosable = (propCloseCollection, contextCloseCollection, fallbackCloseCollection = EmptyFallbackCloseCollection, closeLabel = 'Close') => {
    const propConfig = computeClosableConfig(propCloseCollection?.closable, propCloseCollection?.closeIcon);
    const contextConfig = computeClosableConfig(contextCloseCollection?.closable, contextCloseCollection?.closeIcon);
    const mergedFallback = {
        closeIcon: React.createElement(CloseOutlined, null),
        ...fallbackCloseCollection,
    };
    const mergedConfig = mergeClosableConfigs(propConfig, contextConfig, mergedFallback);
    const closeBtnIsDisabled = typeof mergedConfig !== 'boolean' ? !!mergedConfig?.disabled : false;
    if (mergedConfig === false) {
        return [false, null, closeBtnIsDisabled, {}];
    }
    const [closeIcon, ariaProps] = computeCloseIcon(mergedConfig, mergedFallback, closeLabel);
    return [true, closeIcon, closeBtnIsDisabled, ariaProps];
};
export const useClosable = (propCloseCollection, contextCloseCollection, fallbackCloseCollection = EmptyFallbackCloseCollection) => {
    const [contextLocale] = useLocale('global', defaultLocale.global);
    return React.useMemo(() => {
        return computeClosable(propCloseCollection, contextCloseCollection, {
            closeIcon: React.createElement(CloseOutlined, null),
            ...fallbackCloseCollection,
        }, contextLocale.close);
    }, [propCloseCollection, contextCloseCollection, fallbackCloseCollection, contextLocale.close]);
};
